import { useLinkLogic, useStackNavigate } from '@/app/navigation/stack-links';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ForeignKeyCell } from '@/components/ui/ForeignKeyCell';
import {
  InfoAccordion,
  InfoAccordionItem,
  InfoAccordionRow,
} from '@/components/ui/info-accordion';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsLink, TabsList } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  useUpdateWorkorderMutation,
  useWorkorder,
  useWorkorderLabor,
  useWorkorderLock,
  useWorkorderMaterials,
} from '@/features/workorders/workorders.hooks';
import { useQueryClient } from '@tanstack/react-query';
import {
  Building,
  CheckCircle2,
  Clipboard,
  Clock,
  ExternalLink,
  FileText,
  Info,
  Lock,
  MapPin,
  Navigation,
  Package,
  PenLine,
  Phone,
  User,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ProgressBadge } from './components/ProgressBadge';
import { WorkorderLaborTable } from './components/WorkorderLaborTable';
import { WorkorderMaterialsTable } from './components/WorkorderMaterialsTable';
import {
  adaptActionsForPanel,
  completeWorkorderWithoutSignature,
  getWorkorderActions,
} from './workorders.actions';
import { workorderQueries } from './workorders.queries';
import { useConfirm } from '@/components/ui/use-confirm';
import { formatDate, formatHours } from './workorders.utils';
import { cn } from '@/lib/utils';
import { firstLine } from '@/utils/string';

// ============================================================================
// Section Header Component
// ============================================================================

function SectionHeader({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={cn(
        `text-muted-foreground flex items-center gap-2 text-xs font-semibold
        tracking-wide uppercase`,
        className,
      )}
    >
      {children}
    </h2>
  );
}

// ============================================================================
// Helper functions
// ============================================================================

function calculateTotalHours(
  labor: Array<{ date_start: Date; date_stop: Date; break: number | null }>,
): number {
  return labor.reduce((sum, entry) => {
    const start = new Date(entry.date_start).getTime();
    const stop = new Date(entry.date_stop).getTime();
    const breakHours = entry.break ?? 0;
    const hours = (stop - start) / (1000 * 60 * 60) - breakHours;
    return sum + Math.max(0, hours);
  }, 0);
}

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  const url = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  window.open(url, '_blank');
}

// ============================================================================
// Main Component
// ============================================================================

export const WorkorderDetailsPanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const { workorderId, tab } = params;
  const recnum = parseInt(workorderId);
  const workorderQuery = useWorkorder(recnum);
  const laborQuery = useWorkorderLabor(recnum);
  const materialsQuery = useWorkorderMaterials(recnum);
  const updateMutation = useUpdateWorkorderMutation();
  const navigate = useStackNavigate();
  const makeLink = useLinkLogic();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialog } = useConfirm();

  // Form state for editable fields
  const [report, setReport] = useState<string>('');
  const [internalNote, setInternalNote] = useState<string>('');
  const [travelFee, setTravelFee] = useState(false);
  const [followUp, setFollowUp] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const workorder = workorderQuery.data;
  const labor = laborQuery.data ?? [];
  const materials = materialsQuery.data ?? [];
  const isCompleted = workorder?.completed ?? false;
  // Frozen when completed, concept, cancelled or assigned to someone else
  const { locked: isLocked, reason: lockReason } = useWorkorderLock(workorder);
  const totalHours = useMemo(() => calculateTotalHours(labor), [labor]);

  // Initialize form state when workorder loads
  useMemo(() => {
    if (workorder) {
      setReport(workorder.report ?? '');
      setInternalNote(workorder.internal_note ?? '');
      setTravelFee(workorder.travelfee);
      setFollowUp(workorder.followup);
      setIsDirty(false);
    }
  }, [workorder]);

  // Save changes (extracted for reuse)
  const saveChanges = useCallback(async () => {
    if (!workorder || !isDirty) return;
    await updateMutation.mutateAsync({
      recnum: workorder.recnum,
      data: {
        report: report || null,
        internal_note: internalNote || null,
        travelfee: travelFee,
        followup: followUp,
      },
    });
    setIsDirty(false);
  }, [
    workorder,
    isDirty,
    report,
    internalNote,
    travelFee,
    followUp,
    updateMutation,
  ]);

  // Navigation handlers
  const handleNavigateToSign = useCallback(async () => {
    try {
      // Save dirty changes before navigating
      if (isDirty) {
        await saveChanges();
      }
      const signPath = makeLink(`${basePath}/sign`);
      navigate({ to: signPath });
    } catch (error) {
      toast.error('Failed to save changes', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [isDirty, saveChanges, makeLink, navigate, basePath]);

  const handleCompleteWithoutSignature = useCallback(async () => {
    if (!workorder) return;
    const ok = await confirm({
      title: 'Complete workorder without signature?',
      description: 'This cannot be undone.',
      confirmLabel: 'Complete',
      destructive: true,
    });
    if (!ok) return;
    try {
      // Save dirty changes before completing
      if (isDirty) {
        await saveChanges();
      }
      await completeWorkorderWithoutSignature(workorder.recnum);
      queryClient.invalidateQueries({
        queryKey: workorderQueries.keys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: workorderQueries.keys.detail(workorder.recnum),
      });
      toast.success('Workorder completed');
    } catch (error) {
      toast.error('Failed to complete workorder', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [workorder, isDirty, saveChanges, queryClient]);

  // Save changes (UI handler with toast feedback)
  const handleSave = useCallback(async () => {
    try {
      await saveChanges();
      // toast.success('Changes saved');
    } catch (error) {
      toast.error('Failed to save changes', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [saveChanges]);

  // Get panel actions
  const actions = useMemo(() => {
    if (!workorder) return [];
    const rowActions = getWorkorderActions(
      {
        onNavigateToSign: handleNavigateToSign,
        onCompleteWithoutSignature: handleCompleteWithoutSignature,
      },
      workorder,
      { canEdit: !isLocked },
    );
    return adaptActionsForPanel(rowActions, workorder);
  }, [
    workorder,
    isLocked,
    handleNavigateToSign,
    handleCompleteWithoutSignature,
  ]);

  const title = workorder
    ? `W${workorder.id}: ${workorder.customer_name}`
    : `W${workorderId}`;

  // Format planning dates
  const planningText = useMemo(() => {
    if (!workorder) return null;
    const from = workorder.execution_date
      ? formatDate(workorder.execution_date)
      : null;
    const to = workorder.end_date ? formatDate(workorder.end_date) : null;
    if (from && to && from !== to) {
      return `${from} - ${to}`;
    }
    return from || to || 'Not scheduled';
  }, [workorder]);

  if (!workorder) {
    return (
      <Panel {...panelProps} title={title}>
        <div className="text-muted-foreground py-8 text-center">Loading...</div>
      </Panel>
    );
  }

  return (
    <Panel
      {...panelProps}
      title={title}
      actions={actions}
      headerActions={
        <ProgressBadge
          progress={workorder.progress}
          completed={workorder.completed}
        />
      }
    >
      <ConfirmDialog />
      <div className="flex flex-col gap-6">
        {/* ============================================
            SECTION 1: INFO (Read-only accordion)
            ============================================ */}
        <section>
          <SectionHeader className="mb-3">
            <Info className="h-4 w-4" />
            Info
          </SectionHeader>

          <Card className="gap-0 overflow-hidden py-0">
            <InfoAccordion type="single" collapsible>
              {/* Customer */}
              <InfoAccordionItem value="customer">
                <InfoAccordionRow
                  icon={<Building className="h-4 w-4" />}
                  label="Customer"
                  summary={workorder.customer_name || 'No customer'}
                >
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Company</dt>
                      <dd>{workorder.customer_name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Work</dt>
                      <dd>
                        <ForeignKeyCell
                          value={workorder.werk_id}
                          entityType="work"
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Address</dt>
                      <dd>{workorder.customer_address || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Phone</dt>
                      <dd>
                        {workorder.customer_phone ? (
                          <a
                            href={`tel:${workorder.customer_phone}`}
                            className="text-primary hover:underline"
                          >
                            {workorder.customer_phone}
                          </a>
                        ) : (
                          '—'
                        )}
                      </dd>
                    </div>
                  </dl>
                </InfoAccordionRow>
              </InfoAccordionItem>

              {/* Location */}
              <InfoAccordionItem value="location">
                <InfoAccordionRow
                  icon={<MapPin className="h-4 w-4" />}
                  label="Location"
                  summary={workorder.location_address || 'No location'}
                  actions={
                    workorder.location_address && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          openInGoogleMaps(workorder.location_address!)
                        }
                      >
                        <Navigation className="h-4 w-4" />
                      </Button>
                    )
                  }
                >
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Address</dt>
                      <dd>{workorder.location_address || '—'}</dd>
                    </div>
                    {workorder.location_doorcode && (
                      <div>
                        <dt className="text-muted-foreground">Door Code</dt>
                        <dd className="font-mono text-lg">
                          {workorder.location_doorcode}
                        </dd>
                      </div>
                    )}
                    {workorder.location_phone && (
                      <div>
                        <dt className="text-muted-foreground">Phone</dt>
                        <dd>
                          <a
                            href={`tel:${workorder.location_phone}`}
                            className="text-primary hover:underline"
                          >
                            {workorder.location_phone}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </InfoAccordionRow>
              </InfoAccordionItem>

              {/* Contact */}
              <InfoAccordionItem value="contact">
                <InfoAccordionRow
                  icon={<User className="h-4 w-4" />}
                  label="Contact"
                  summary={workorder.contact_name || 'No contact'}
                  actions={
                    workorder.contact_phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        tooltip={false}
                      >
                        <a href={`tel:${workorder.contact_phone}`}>
                          <Phone className="h-4 w-4" />
                        </a>
                      </Button>
                    )
                  }
                >
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Contact Person</dt>
                      <dd>{workorder.contact_name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Phone</dt>
                      <dd>
                        {workorder.contact_phone ? (
                          <a
                            href={`tel:${workorder.contact_phone}`}
                            className="text-primary hover:underline"
                          >
                            {workorder.contact_phone}
                          </a>
                        ) : (
                          '—'
                        )}
                      </dd>
                    </div>
                  </dl>
                </InfoAccordionRow>
              </InfoAccordionItem>

              {/* Task */}
              <InfoAccordionItem value="task" isLast>
                <InfoAccordionRow
                  icon={<Clipboard className="h-4 w-4" />}
                  label="Task"
                  summary={firstLine(workorder.description || 'No description')}
                >
                  <div className="space-y-3 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Description</dt>
                      <dd className="mt-1 whitespace-pre-wrap">
                        {workorder.description || '—'}
                      </dd>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <div>
                        <dt className="text-muted-foreground">Planned</dt>
                        <dd>{planningText}</dd>
                      </div>
                      {workorder.creator_name && (
                        <div>
                          <dt className="text-muted-foreground">Created by</dt>
                          <dd>{workorder.creator_name}</dd>
                        </div>
                      )}
                      {workorder.technician_name && (
                        <div>
                          <dt className="text-muted-foreground">Technician</dt>
                          <dd>{workorder.technician_name}</dd>
                        </div>
                      )}
                    </div>
                  </div>
                </InfoAccordionRow>
              </InfoAccordionItem>
            </InfoAccordion>
          </Card>
        </section>

        {/* ============================================
            SECTION 2: WORK LOG (Tabs)
            ============================================ */}
        <section>
          <SectionHeader className="mb-3">
            <FileText className="h-4 w-4" />
            Work Log
          </SectionHeader>

          <Card className="p-0">
            <Tabs value={tab || 'notes'} className="w-full">
              <div className="p-2 pb-0">
                <TabsList scrollable className="w-full">
                  <TabsLink
                    to={`${basePath}/notes`}
                    value="notes"
                    className="flex-1"
                  >
                    <FileText className="mr-1 h-4 w-4" />
                    Report
                  </TabsLink>
                  <TabsLink
                    to={`${basePath}/labor`}
                    value="labor"
                    className="flex-1"
                  >
                    <Clock className="mr-1 h-4 w-4" />
                    Labor ({labor.length > 0 ? formatHours(totalHours) : '—'})
                  </TabsLink>
                  <TabsLink
                    to={`${basePath}/materials`}
                    value="materials"
                    className="flex-1"
                  >
                    <Package className="mr-1 h-4 w-4" />
                    Materials ({materials.length || '—'})
                  </TabsLink>
                </TabsList>
              </div>

              <div className="p-4">
                <TabsContent value="notes" className="mt-0 space-y-4">
                  {/* Work Completed */}
                  <div className="space-y-2">
                    <Label htmlFor="report">Work Completed</Label>
                    {isLocked ? (
                      <p className="text-sm whitespace-pre-wrap">
                        {workorder.report || 'No report provided'}
                      </p>
                    ) : (
                      <div className="-mx-4 sm:mx-0">
                        <Textarea
                          id="report"
                          placeholder="Describe the work performed..."
                          className="bg-muted/30 min-h-[100px] rounded-none border-x-0 sm:rounded-md sm:border-x"
                          value={report}
                          onChange={(e) => {
                            setReport(e.target.value);
                            setIsDirty(true);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Internal Note */}
                  <div className="space-y-2">
                    <Label htmlFor="internal-note">Internal Note</Label>
                    {isLocked ? (
                      <p className="text-muted-foreground text-sm whitespace-pre-wrap">
                        {workorder.internal_note || 'No internal note'}
                      </p>
                    ) : (
                      <div className="-mx-4 sm:mx-0">
                        <Textarea
                          id="internal-note"
                          placeholder="Notes for internal use only..."
                          className="bg-muted/30 min-h-[80px] rounded-none border-x-0 sm:rounded-md sm:border-x"
                          value={internalNote}
                          onChange={(e) => {
                            setInternalNote(e.target.value);
                            setIsDirty(true);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Flags */}
                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="travel-fee"
                        checked={travelFee}
                        disabled={isLocked}
                        onCheckedChange={(checked) => {
                          setTravelFee(checked);
                          setIsDirty(true);
                        }}
                      />
                      <Label
                        htmlFor="travel-fee"
                        className="text-sm font-normal"
                      >
                        Travel Fee
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="followup"
                        checked={followUp}
                        disabled={isLocked}
                        onCheckedChange={(checked) => {
                          setFollowUp(checked);
                          setIsDirty(true);
                        }}
                      />
                      <Label htmlFor="followup" className="text-sm font-normal">
                        Follow-up Required
                      </Label>
                    </div>
                  </div>

                  {/* Save button when dirty */}
                  {isDirty && !isLocked && (
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleSave}
                        disabled={updateMutation.isPending}
                      >
                        {updateMutation.isPending
                          ? 'Saving...'
                          : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="labor" className="mt-0">
                  <WorkorderLaborTable
                    workorderRecnum={recnum}
                    basePath={basePath}
                    labor={labor}
                    isLoading={laborQuery.isLoading}
                    isLocked={isLocked}
                  />
                </TabsContent>

                <TabsContent value="materials" className="mt-0">
                  <WorkorderMaterialsTable
                    workorderRecnum={recnum}
                    basePath={basePath}
                    materials={materials}
                    isLoading={materialsQuery.isLoading}
                    isLocked={isLocked}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </Card>
        </section>

        {/* ============================================
            SECTION 3: COMPLETION
            ============================================ */}
        <section>
          <SectionHeader className="mb-3">
            <CheckCircle2 className="h-4 w-4" />
            Completion
          </SectionHeader>

          {isCompleted ? (
            /* Completed state: show signature info and link to report */
            <div className="space-y-4">
              <Card className="p-4">
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Completed</dt>
                    <dd className="font-medium">
                      {workorder.date_completed
                        ? formatDate(workorder.date_completed)
                        : 'Unknown'}
                    </dd>
                  </div>
                  {workorder.email && (
                    <div>
                      <dt className="text-muted-foreground">Report sent to</dt>
                      <dd>{workorder.email}</dd>
                    </div>
                  )}
                  {workorder.signed && workorder.signature_data && (
                    <div className="col-span-2">
                      <dt className="text-muted-foreground mb-2">Signature</dt>
                      <dd className="bg-muted/20 flex h-24 items-center justify-center rounded-md border">
                        <img
                          src={workorder.signature_data}
                          alt="Customer signature"
                          className="max-h-full max-w-full object-contain"
                        />
                      </dd>
                    </div>
                  )}
                </dl>
              </Card>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleNavigateToSign}
              >
                <FileText className="mr-2 h-4 w-4" />
                View Report
              </Button>
            </div>
          ) : isLocked ? (
            /* Open but frozen for this user: explain instead of offering CTAs */
            <Card className="flex-row items-start gap-3 p-4">
              <Lock
                className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <p className="text-muted-foreground text-sm">{lockReason}</p>
            </Card>
          ) : (
            /* Open state: show CTA buttons */
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCompleteWithoutSignature}
              >
                Complete without Signature
              </Button>
              <Button className="flex-1" onClick={handleNavigateToSign}>
                <PenLine className="mr-2 h-4 w-4" />
                Sign & Complete
              </Button>
            </div>
          )}
        </section>
      </div>
    </Panel>
  );
};
